// ============================================================================
// ORDERS · the Book's year / sub-collection axis (B1a)
// ============================================================================
// ONE predicate, applied at the same seams where `scope` and the Data pills
// already filter (SummaryView's scoped set and useGridView's filtered set) —
// never a second copy. Everything here is pure and reads only `collection`:
//   SS2027        → year 2027 · sub main
//   SS2027-Pre    → year 2027 · sub pre
//   SS2026-Distributors / -Thailand → distributors / thailand
//   AW22-23       → no four-digit year → belongs to All only · sub aw
// A row without a parseable year belongs to All only, by design — a wrong
// year is worse than an honest absence.
// Sun / Optic is the third axis (B1.5 / B1.6): live rows carry `book` since
// orders-optic-live-v1 (07.09.2026); `bookOf` below reads it.
// ============================================================================

// The upcoming season remains selectable before its first Book import.
export const BOOK_PLANNING_YEAR = 2028;

export const collectionOf = (row) => String(row?.collection ?? '').trim();

/** Four-digit year inside the collection value, or null. */
export function bookYearOf(row) {
  const m = /(\d{4})/.exec(collectionOf(row));
  return m ? m[1] : null;
}

/** 'main' | 'pre' | 'distributors' | 'thailand' | 'aw' — the suffix after the year. */
export function bookSubOf(row) {
  const c = collectionOf(row);
  if (/^AW/i.test(c)) return 'aw';
  const tail = c.replace(/^[A-Za-z]*\d{4}/, '').replace(/^[-\s_]+/, '').toLowerCase();
  if (!tail) return 'main';
  if (tail.startsWith('pre')) return 'pre';
  if (tail.startsWith('distrib')) return 'distributors';
  if (tail.startsWith('thai')) return 'thailand';
  return 'main';
}

// ── B1.5 · the book axis and the merge rule ─────────────────────────────────
export const BOOK_KINDS = ['sun', 'optic'];
export const BOOK_KIND_LABEL = { sun: 'Sun', optic: 'Optic' };

/** Which book a row belongs to. `orders_lines.book` is not null, default sun
 *  (B1.6); a row that somehow lacks the key still reads as the Sun book. */
export const bookOf = (row) => (row?.book === 'optic' ? 'optic' : 'sun');

/**
 * Live ∪ history, ONE rule: a (book, collection) that has rows in the live
 * table belongs to the daily import, and every history row of that pair is
 * dropped — the same exclusion the landing package applies at insert time,
 * enforced again at read time so a stale landing can never double-count.
 * Live rows are tagged `source:'live'`, history rows `source:'history'`.
 * CLB-078 · ONE exception: a history line a person corrected (`updated_by` is
 * set — the landing never sets it) stays, tagged `carried:true`, even when its
 * (book, collection) is live-owned. Moving a line into a live season must never
 * make it vanish; it is a distinct line, so nothing is counted twice.
 */
export function mergeBookRows(liveRows, historyRows) {
  const live = (liveRows || []).map((r) => (r?.source === 'live' ? r : { ...r, source: 'live' }));
  if (!historyRows || !historyRows.length) return live;
  const owned = new Set(live.map((r) => `${bookOf(r)}|${collectionOf(r)}`));
  const hist = historyRows
    .filter((r) => r?.updated_by || !owned.has(`${bookOf(r)}|${collectionOf(r)}`))
    .map((r) => {
      const t = r?.source === 'history' ? r : { ...r, source: 'history' };
      return owned.has(`${bookOf(r)}|${collectionOf(r)}`) ? { ...t, carried: true } : t;
    });
  return live.concat(hist);
}

/** The one predicate. `year` is 'all' or a four-digit string; `sub` is 'all'
 *  or a sub key; `book` is 'sun' | 'optic' (absent = no book filter). */
export function bookRowInScope(row, { year, years, sub, book } = {}) {
  if (book && bookOf(row) !== book) return false;
  // `years` (a chosen set, v8 Years tray) wins over the single `year`; a row without a year is under All only
  if (Array.isArray(years) && years.length) {
    if (!years.includes('all')) { const y = bookYearOf(row); if (!y || !years.includes(y)) return false; }
  } else if (year && year !== 'all' && bookYearOf(row) !== year) return false;
  if (sub && sub !== 'all' && bookSubOf(row) !== sub) return false;
  return true;
}

/** Fixed list 2023 … at least the 2028 planning season, unioned with every year found in the rows. */
export function bookYears(rows) {
  const out = new Set();
  const top = Math.max(BOOK_PLANNING_YEAR, new Date().getFullYear() + 1);
  for (let y = 2023; y <= top; y += 1) out.add(String(y));
  (rows || []).forEach((r) => { const y = bookYearOf(r); if (y) out.add(y); });
  return [...out].sort();
}

/** Per-year row counts, plus the latest year that has rows (null when none). */
export function bookYearCounts(rows) {
  const counts = {};
  (rows || []).forEach((r) => { const y = bookYearOf(r); if (y) counts[y] = (counts[y] || 0) + 1; });
  const years = Object.keys(counts).sort();
  return { counts, latest: years.length ? years[years.length - 1] : null };
}

/** Distinct sub keys present within a year ('all' means every year), in rail order. */
export function bookSubs(rows, year) {
  const seen = new Set();
  const list = Array.isArray(year) ? year : null;
  (rows || []).forEach((r) => {
    if (list ? !(list.includes('all') || list.includes(bookYearOf(r))) : (year && year !== 'all' && bookYearOf(r) !== year)) return;
    seen.add(bookSubOf(r));
  });
  return ['main', 'pre', 'distributors', 'thailand', 'aw'].filter((k) => seen.has(k));
}

export const BOOK_SUB_LABEL = {
  all: 'All', main: 'Main', pre: 'Pre', distributors: 'Distributors', thailand: 'Thailand', aw: 'AW',
};

/** The address / store form of a year choice: null | 'all' | '2027' | '2026,2027' → the list of chosen years
 *  (['all'] for All or an unreadable value; [] never). Sorted, de-duplicated. */
export function parseYearKey(key) {
  if (!key || key === 'all') return ['all'];
  const ys = [...new Set(String(key).split(',').map((y) => y.trim()).filter((y) => /^\d{4}$/.test(y)))].sort();
  return ys.length ? ys : ['all'];
}

/** The inverse: a list of years → the key stored on the address ('all' when empty or All is in it). */
export function yearKeyOf(list) {
  const ys = [...new Set((list || []).filter((y) => /^\d{4}$/.test(y)))].sort();
  return ys.length ? ys.join(',') : 'all';
}
